import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { formatKr, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { RECURRING_STATUS, frequencyShort } from "@/lib/recurringConstants";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Repeat, Plus, Send, CheckCircle2, AlertTriangle, Loader2, MoreHorizontal, Pencil, PauseCircle, PlayCircle, Trash2, Zap, ExternalLink, MailX, Info, BadgeCheck, ArrowRight } from "lucide-react";

const StatusBadge = ({ status, testId }) => {
  const s = RECURRING_STATUS[status] || RECURRING_STATUS.aktiv;
  const Icon = s.icon;
  return <Badge variant="outline" className={cn("border gap-1", s.cls)} data-testid={testId}><Icon className="h-3 w-3" /> {s.label}</Badge>;
};

/**
 * Faktura → Gjentakende: kladder klare for godkjenning (batch-utstedelse) + avtaler (opprett/rediger/pause/slett).
 */
export default function RecurringPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind: "delete-agreement" | "delete-draft", item }

  const load = useCallback(() => {
    setError("");
    return api.get("/sales/recurring")
      .then((r) => {
        setData(r.data);
        setSelected((prev) => {
          const sel = {};
          for (const d of r.data.pending) sel[d.id] = prev[d.id] ?? true;
          return sel;
        });
      })
      .catch((e) => setError(errorMessage(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const pending = useMemo(() => data?.pending || [], [data]);
  const agreements = useMemo(() => data?.items || [], [data]);
  const chosen = useMemo(() => pending.filter((d) => selected[d.id]), [pending, selected]);
  const chosenEmailable = chosen.filter((d) => d.customer?.email);
  const allSelected = pending.length > 0 && chosen.length === pending.length;
  const totalChosen = chosen.reduce((s, d) => s + Number(d.total || 0), 0);
  const profileOk = data?.profile_completeness?.complete;

  const refreshBadges = () => { window.dispatchEvent(new Event("rp:refresh-recurring")); window.dispatchEvent(new Event("rp:refresh-reminders")); };

  const approve = async (sendEmail) => {
    const list = sendEmail ? chosenEmailable : chosen;
    if (!list.length) return;
    setBusy(sendEmail ? "send" : "issue");
    setResult(null);
    try {
      const r = await api.post("/sales/recurring/approve-batch", { invoice_ids: list.map((d) => d.id), send_email: sendEmail });
      setResult({ ...r.data, sendEmail });
      if (r.data.failed_count === 0) toast.success(`${r.data.ok_count} faktura${r.data.ok_count === 1 ? "" : "er"} utstedt${sendEmail ? " og sendt" : ""}`);
      else toast.warning(`${r.data.ok_count} OK, ${r.data.failed_count} feilet`, { description: "Se detaljer under." });
      await load();
      refreshBadges();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg);
      setConfirm(null);
      await load();
      refreshBadges();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const pauseResume = (ag) => act(`pr-${ag.id}`, () => api.post(`/sales/recurring/${ag.id}/${ag.status === "aktiv" ? "pause" : "resume"}`), ag.status === "aktiv" ? "Avtalen er pauset" : "Avtalen er aktiv igjen");
  const generateNow = (ag) => act(`gen-${ag.id}`, () => api.post(`/sales/recurring/${ag.id}/generate-now`), (r) => `Kladd for ${r.data.draft?.recurring_period_label || "neste periode"} er klar`);
  const deleteAgreement = (ag) => act(`del-${ag.id}`, () => api.delete(`/sales/recurring/${ag.id}`), "Avtalen er slettet. Eksisterende kladder og fakturaer er beholdt.");
  const deleteDraft = (d) => act(`deld-${d.id}`, () => api.delete(`/sales/invoices/${d.id}`), "Kladden er slettet. Perioden lages ikke på nytt.");

  if (error) return <EmptyState icon={AlertTriangle} title="Kunne ikke laste gjentakende fakturaer" description={error} actionLabel="Prøv igjen" onAction={load} testId="recurring-error" />;
  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-72" /><Skeleton className="h-48" /><Skeleton className="h-64" /></div>;

  const active = agreements.filter((a) => a.status === "aktiv");

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Faktura"
        title="Gjentakende fakturaer"
        description={agreements.length ? `${active.length} aktiv${active.length === 1 ? "" : "e"} avtale${active.length === 1 ? "" : "r"}${pending.length ? ` · ${pending.length} kladd${pending.length === 1 ? "" : "er"} venter på godkjenning` : " · alt er godkjent"}.` : "Sett opp faste fakturaer til kunder – Controlle lager kladden, du godkjenner."}
        actions={<Button asChild data-testid="recurring-new-button"><Link to="/faktura/gjentakende/ny"><Plus className="h-4 w-4 mr-2" /> Ny avtale</Link></Button>}
      />

      {!profileOk && pending.length ? (
        <Alert className="border-[#FFE2A3] dark:border-[#8A5A00]/50" data-testid="recurring-profile-incomplete">
          <BadgeCheck className="h-4 w-4" />
          <AlertTitle>Fullfør firmaprofilen før du utsteder</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1"><span>Mangler: {data.profile_completeness.missing.join(", ")}.</span><Button variant="link" className="h-auto p-0" onClick={() => navigate("/profil")}>Gå til profil <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button></AlertDescription>
        </Alert>
      ) : null}

      {result ? (
        <Alert className={cn(result.failed_count ? "border-warn/40" : "border-success/40")} data-testid="recurring-result">
          {result.failed_count ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          <AlertTitle>{result.sendEmail ? "Fakturaer utstedt og sendt" : "Fakturaer utstedt"}</AlertTitle>
          <AlertDescription>
            <p>{result.ok_count} OK{result.failed_count ? `, ${result.failed_count} feilet` : ""}.</p>
            <ul className="mt-2 space-y-1 text-xs">
              {result.results.map((r) => (
                <li key={r.invoice_id} className="flex items-center gap-2" data-testid={`recurring-result-${r.invoice_id}`}>
                  {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <AlertTriangle className="h-3.5 w-3.5 text-flag" />}
                  <span>
                    {r.ok ? <>Faktura <Link to={`/faktura/fakturaer/${r.invoice_id}`} className="underline">{r.number_display}</Link> til {r.customer_name}{r.period_label ? ` (${r.period_label})` : ""}{r.sent_to ? ` – sendt til ${r.sent_to}` : ""}{r.error ? ` – ${r.error}` : ""}</> : `${r.customer_name || "Kladd"}: ${r.error}`}
                  </span>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Kladder klare for godkjenning */}
      {pending.length > 0 ? (
        <Card data-testid="recurring-pending-card">
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <CardTitle className="text-base">Klare for godkjenning</CardTitle>
                <CardDescription>Kontroller kladdene, velg hvilke som skal utstedes, og utsted alle i én omgang. Fakturanummer tildeles ved utstedelse.</CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => approve(false)} disabled={!!busy || !profileOk || chosen.length === 0} data-testid="recurring-approve-button">
                  {busy === "issue" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Utsted {chosen.length}
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button variant="outline" onClick={() => approve(true)} disabled={!!busy || !profileOk || !data.email_configured || chosenEmailable.length === 0} data-testid="recurring-approve-send-button">
                        {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />} Utsted og send {chosenEmailable.length} på e-post
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {!data.email_configured ? <TooltipContent>E-post er ikke satt opp av administrator. Utsted og last ned PDF fra fakturaen i stedet.</TooltipContent> : chosenEmailable.length < chosen.length ? <TooltipContent>{chosen.length - chosenEmailable.length} valgt{chosen.length - chosenEmailable.length === 1 ? "" : "e"} kunde{chosen.length - chosenEmailable.length === 1 ? "" : "r"} mangler e-post og hoppes over.</TooltipContent> : null}
                </Tooltip>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="border-t px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground bg-secondary/40">
              <label className="inline-flex items-center gap-2 text-sm text-foreground">
                <Checkbox checked={allSelected} onCheckedChange={(v) => { const sel = {}; if (v) for (const d of pending) sel[d.id] = true; setSelected(sel); }} aria-label="Velg alle" data-testid="recurring-select-all" /> Velg alle
              </label>
              <span data-testid="recurring-summary">Valgt: <strong className="text-foreground tnum">{chosen.length}</strong> · sum <strong className="text-foreground tnum">{formatKr(totalChosen)}</strong></span>
              {!data.email_configured ? <span className="inline-flex items-center gap-1"><Info className="h-3.5 w-3.5" /> E-post er ikke satt opp – fakturaene utstedes og kan lastes ned som PDF.</span> : null}
            </div>
            <ul className="divide-y" data-testid="recurring-pending-list">
              {pending.map((d) => {
                const checked = !!selected[d.id];
                return (
                  <li key={d.id} className={cn("px-4 sm:px-6 py-3 flex flex-col lg:flex-row lg:items-center gap-3", !checked && "opacity-60")} data-testid={`recurring-draft-row-${d.id}`}>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Checkbox checked={checked} onCheckedChange={(v) => setSelected((sel) => ({ ...sel, [d.id]: !!v }))} aria-label={`Velg kladd for ${d.customer?.name}`} className="mt-1" data-testid={`recurring-draft-select-${d.id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link to={`/faktura/fakturaer/${d.id}`} className="font-medium hover:underline truncate" data-testid={`recurring-draft-link-${d.id}`}>{d.customer?.name || "Ukjent kunde"}</Link>
                          {d.recurring_period_label ? <Badge variant="outline" className="status-til_betaling border" data-testid={`recurring-draft-period-${d.id}`}>{d.recurring_period_label}</Badge> : null}
                          {!d.customer?.email ? <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center gap-1 text-xs text-warn" data-testid={`recurring-draft-noemail-${d.id}`}><MailX className="h-3.5 w-3.5" /> mangler e-post</span></TooltipTrigger><TooltipContent>Kunden har ingen e-postadresse. Fakturaen kan utstedes og lastes ned som PDF.</TooltipContent></Tooltip> : null}
                        </div>
                        <p className="text-xs text-muted-foreground">{d.recurring_name ? `${d.recurring_name} · ` : ""}fakturadato {formatDate(d.issue_date)} · forfall {formatDate(d.due_date)}{d.recurring_deleted ? " · avtalen er slettet" : ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between lg:justify-end gap-3">
                      <p className="font-medium tnum" data-testid={`recurring-draft-total-${d.id}`}>{formatKr(d.total)}</p>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/faktura/fakturaer/${d.id}/rediger`)} data-testid={`recurring-draft-edit-${d.id}`}><Pencil className="h-4 w-4 mr-1.5" /> Rediger</Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => setConfirm({ kind: "delete-draft", item: d })} aria-label="Slett kladd" data-testid={`recurring-draft-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      ) : agreements.length ? (
        <Alert data-testid="recurring-all-approved"><CheckCircle2 className="h-4 w-4" /><AlertTitle>Ingen kladder venter</AlertTitle><AlertDescription>Neste kladd lages automatisk {active.length ? `${formatDate(active.map((a) => a.next_generate_on).filter(Boolean).sort()[0])}` : "når en avtale er aktiv"}. Du får varsel når den er klar.</AlertDescription></Alert>
      ) : null}

      {/* Avtaler */}
      {agreements.length === 0 ? (
        <EmptyState icon={Repeat} title="Ingen gjentakende avtaler ennå" description="Sett opp en avtale for kunder du fakturerer fast – husleie, abonnement, vedlikehold eller timer. Controlle lager en kladd før hver fakturadato, og du godkjenner før noe sendes." actionLabel="Opprett første avtale" actionTo="/faktura/gjentakende/ny" testId="recurring-empty" />
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Avtaler</CardTitle>
            <CardDescription>{data.counts.aktiv} aktiv{data.counts.aktiv === 1 ? "" : "e"}{data.counts.pauset ? ` · ${data.counts.pauset} pauset` : ""}{data.counts.avsluttet ? ` · ${data.counts.avsluttet} avsluttet` : ""}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y border-t" data-testid="recurring-agreements-list">
              {agreements.map((ag) => (
                <li key={ag.id} className="px-4 sm:px-6 py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`recurring-agreement-row-${ag.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/faktura/gjentakende/${ag.id}/rediger`} className="font-medium hover:underline truncate" data-testid={`recurring-agreement-link-${ag.id}`}>{ag.name}</Link>
                      <StatusBadge status={ag.status} testId={`recurring-agreement-status-${ag.id}`} />
                      <Badge variant="secondary" className="text-[11px]" data-testid={`recurring-agreement-frequency-${ag.id}`}>{frequencyShort(ag.frequency)}</Badge>
                      {ag.pending_drafts ? <Badge variant="outline" className="status-ma_kontrolleres border text-[11px]" data-testid={`recurring-agreement-pending-${ag.id}`}>{ag.pending_drafts} kladd{ag.pending_drafts === 1 ? "" : "er"} venter</Badge> : null}
                    </div>
                    <p className="text-xs text-muted-foreground truncate" data-testid={`recurring-agreement-meta-${ag.id}`}>
                      {ag.customer_name}
                      {ag.status === "aktiv" && ag.next_date ? ` · neste faktura ${formatDate(ag.next_date)} (kladd lages ${formatDate(ag.next_generate_on)})` : ""}
                      {ag.status === "pauset" ? ` · pauset${ag.pause_reason ? ` – ${ag.pause_reason}` : ""}` : ""}
                      {ag.status === "avsluttet" ? ` · avsluttet ${ag.end_date ? formatDate(ag.end_date) : ""}` : ""}
                      {ag.generated_count ? ` · ${ag.generated_count} laget` : ""}
                    </p>
                  </div>
                  <div className="flex items-center justify-between lg:justify-end gap-3">
                    <p className="font-medium tnum whitespace-nowrap" data-testid={`recurring-agreement-total-${ag.id}`}>{formatKr(ag.total)} <span className="text-xs text-muted-foreground font-normal">/ faktura</span></p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Handlinger" data-testid={`recurring-agreement-menu-${ag.id}`}>{busy.endsWith(ag.id) ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}</Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card w-56">
                        <DropdownMenuItem onClick={() => navigate(`/faktura/gjentakende/${ag.id}/rediger`)} data-testid={`recurring-agreement-edit-${ag.id}`}><Pencil className="h-4 w-4 mr-2" /> Rediger avtale</DropdownMenuItem>
                        {ag.status === "aktiv" ? <DropdownMenuItem onClick={() => generateNow(ag)} data-testid={`recurring-agreement-generate-${ag.id}`}><Zap className="h-4 w-4 mr-2" /> Lag kladd nå</DropdownMenuItem> : null}
                        {ag.status !== "avsluttet" ? <DropdownMenuItem onClick={() => pauseResume(ag)} data-testid={`recurring-agreement-pause-${ag.id}`}>{ag.status === "aktiv" ? <><PauseCircle className="h-4 w-4 mr-2" /> Pause avtalen</> : <><PlayCircle className="h-4 w-4 mr-2" /> Fortsett avtalen</>}</DropdownMenuItem> : null}
                        <DropdownMenuItem onClick={() => navigate(`/faktura/fakturaer?q=${encodeURIComponent(ag.customer_name || "")}`)} data-testid={`recurring-agreement-invoices-${ag.id}`}><ExternalLink className="h-4 w-4 mr-2" /> Se fakturaer</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirm({ kind: "delete-agreement", item: ag })} data-testid={`recurring-agreement-delete-${ag.id}`}><Trash2 className="h-4 w-4 mr-2" /> Slett avtale</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">Kladder lages automatisk det antall dager før fakturadatoen du har valgt i avtalen, og du varsles i appen{data.email_configured ? " og på e-post" : ""}. Ingenting utstedes eller sendes uten at du godkjenner.</p>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-card" data-testid="recurring-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "delete-agreement" ? `Slette avtalen «${confirm.item.name}»?` : "Slette kladden?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "delete-agreement" ? "Det lages ikke flere kladder for denne avtalen. Kladder og fakturaer som allerede er laget beholdes." : `Kladden for ${confirm?.item?.customer?.name || "kunden"}${confirm?.item?.recurring_period_label ? ` (${confirm.item.recurring_period_label})` : ""} slettes og lages ikke på nytt for samme periode.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recurring-confirm-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => (confirm.kind === "delete-agreement" ? deleteAgreement(confirm.item) : deleteDraft(confirm.item))} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="recurring-confirm-delete">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
